import { Fragment } from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import type { CommandSection, PaletteCommand } from "@/lib/commands"
import { COMMAND_SECTIONS } from "@/lib/commands"

// The `>` command-mode list (CON-184). Renders the filtered
// commands grouped into CREATE / NAVIGATE sections (Paper
// artboard 4F5 desktop / 58C mobile). The palette owns the
// keyboard-active index across the FLAT list, so ↑/↓ glide
// across section boundaries; this component only renders +
// reports hover / activation.

interface CommandListProps {
  // Already filtered by the text typed after `>`, in registry
  // order (CREATE rows first, then NAVIGATE).
  commands: PaletteCommand[]
  // Index of the keyboard-active row within `commands`.
  activeIndex: number
  onHoverIndex: (index: number) => void
  onRun: (command: PaletteCommand) => void
  // The text after `>` — shown in the empty state.
  filter: string
}

export function CommandList({
  commands,
  activeIndex,
  onHoverIndex,
  onRun,
  filter,
}: CommandListProps) {
  const { t, i18n } = useLingui()
  const sectionLabel = Object.fromEntries(
    COMMAND_SECTIONS.map((entry) => [
      entry.section,
      i18n._(entry.label),
    ]),
  ) as Record<CommandSection, string>
  if (commands.length === 0) {
    return (
      <div
        data-testid="command-empty-state"
        className="flex flex-col items-center gap-1 px-6 py-10 text-center"
      >
        <span className="text-[13px] leading-[18px] font-medium text-[#1E1B17]">
          {t`No command matches "${filter}"`}
        </span>
        <span className="text-[12px] leading-4 text-[#9A938A]">
          <Trans>Try "deal", "inbox", or "settings".</Trans>
        </span>
      </div>
    )
  }

  return (
    <div data-testid="command-list" className="flex flex-col">
      {commands.map((command, index) => {
        const isFirstOfSection =
          index === 0 || commands[index - 1].section !== command.section
        return (
          <Fragment key={command.id}>
            {isFirstOfSection ? (
              <SectionLabel>{sectionLabel[command.section]}</SectionLabel>
            ) : null}
            <CommandRow
              command={command}
              active={index === activeIndex}
              onHover={() => onHoverIndex(index)}
              onRun={() => onRun(command)}
            />
          </Fragment>
        )
      })}
    </div>
  )
}

function CommandRow({
  command,
  active,
  onHover,
  onRun,
}: {
  command: PaletteCommand
  active: boolean
  onHover: () => void
  onRun: () => void
}) {
  const { i18n } = useLingui()
  const label = i18n._(command.label)
  const hint = command.hint ? i18n._(command.hint) : null
  return (
    <button
      type="button"
      onClick={onRun}
      onMouseEnter={onHover}
      aria-label={label}
      data-testid={`command-row-${command.id}`}
      data-active={active ? "true" : "false"}
      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left ${
        active ? "bg-[#FBF3EA]" : "hover:bg-[#FBF8F1]"
      }`}
    >
      <span
        aria-hidden="true"
        className="flex size-7 shrink-0 items-center justify-center rounded-md border border-[#F0E5D2] bg-[#FBF0E4] font-semibold text-brand"
      >
        {">"}
      </span>
      <span className="min-w-0 grow truncate text-[13.5px] leading-[18px] text-[#1E1B17]">
        {label}
      </span>
      {hint ? (
        <span className="shrink-0 text-[12px] leading-4 text-[#9A938A]">
          {hint}
        </span>
      ) : null}
    </button>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-0.5 pt-2 text-[10px] uppercase leading-3 font-semibold tracking-[0.08em] text-[#9A938A]">
      {children}
    </div>
  )
}
